'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { ACCOUNT_INACTIVE_MESSAGE, SESSION_ENDED_MESSAGE } from '@/lib/auth/session-policy';
import { safeNextFromSearch } from '@/lib/auth/safe-next';

const RATE_LIMIT_COOLDOWN = 60;

// SEC-002 / USR-003 D4: `?next=` is hostile input. The check (and the reasons for each rule) is
// `safeNextPath` in @/lib/auth/safe-next, shared with the auth callback and the forced password
// change; anything it refuses lands on the dashboard.

export default function LoginPage() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [accountInactive, setAccountInactive] = useState(false);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // AUTH-002: the app (idle sign-out, wake after 15 minutes) or Auth (revoked or
    // expired session) ended the session and sent us here with `?reason=expired`.
    // Read from window.location so this page needs no Suspense boundary.
    // SEC-003 I2.1: `?reason=inactive` — a route answered 403 ACCOUNT_INACTIVE and the app signed out.
    const reason = new URLSearchParams(window.location.search).get('reason');
    setSessionEnded(reason === 'expired');
    setAccountInactive(reason === 'inactive');
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const startCooldown = () => {
    setCooldown(RATE_LIMIT_COOLDOWN);
    cooldownRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || cooldown > 0) return;
    setError('');
    setSessionEnded(false);
    setLoading(true);
    try {
      await signIn(email, password);
      // SEC-002. Read from window.location rather than useSearchParams so this
      // page needs no Suspense boundary. `?next=` is validated as hostile input
      // and falls back to the dashboard — see safeNextPath.
      router.replace(safeNextFromSearch(window.location.search));
    } catch (err: any) {
      const msg: string = err?.message || '';
      if (
        msg.toLowerCase().includes('rate limit') ||
        msg.toLowerCase().includes('too many requests') ||
        err?.status === 429
      ) {
        setError(`Too many sign-in attempts. Please wait ${RATE_LIMIT_COOLDOWN} seconds before trying again.`);
        startCooldown();
      } else {
        setError(msg || 'Invalid email or password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = loading || cooldown > 0;

  return (
    <div className="min-h-screen bg-base-200 flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/pugmed-logo.svg"
            alt="PugMed Clinic Inventory Management"
            className="h-44 w-auto mx-auto"
          />
        </div>

        {/* Card */}
        <div className="bg-base-100 rounded-2xl shadow-lg p-8">
          <h2 className="text-xl font-semibold text-base-content mb-1">Sign in to your account</h2>
          <p className="text-base-content/60 text-sm mb-6">Enter your credentials to continue</p>

          {sessionEnded && !error && (
            <div role="status" className="alert alert-info mb-4 py-3 text-sm">
              <span>{SESSION_ENDED_MESSAGE}</span>
            </div>
          )}

          {accountInactive && !error && (
            <div role="status" className="alert alert-warning mb-4 py-3 text-sm">
              <span>{ACCOUNT_INACTIVE_MESSAGE}</span>
            </div>
          )}

          {error && (
            <div className="alert alert-error mb-4 py-3 text-sm">
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-control">
              <label className="label pb-1">
                <span className="label-text font-medium">Email address</span>
              </label>
              <input
                type="email"
                className="input input-bordered w-full"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                disabled={isDisabled}
              />
            </div>

            <div className="form-control">
              <label className="label pb-1">
                <span className="label-text font-medium">Password</span>
              </label>
              <input
                type="password"
                className="input input-bordered w-full"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                disabled={isDisabled}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary w-full mt-2"
              disabled={isDisabled}
            >
              {loading ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Signing in...
                </>
              ) : cooldown > 0 ? (
                `Try again in ${cooldown}s`
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          <p className="text-center text-sm text-base-content/60 mt-6">
            Contact your administrator to request access.
          </p>
        </div>
      </div>
    </div>
  );
}
